import path from "path"
import { Command } from "commander"
import { z } from "zod"
import select from "@/src/inquirer/select"
import { Separator, input, confirm, checkbox } from "@inquirer/prompts"
import { preFlightAdd } from "@/src/preflights/preflight-add"
import { addComponents } from "@/src/utils/add-components"
import * as ERRORS from "@/src/utils/errors"
import { handleError } from "@/src/utils/handle-error"
import { logger } from "@/src/utils/logger"
import { getRegistryIndex } from "@/src/utils/registry"
import { colors } from "@/src/utils/colors"
import type { RegistryItemIndexSchema } from "@/src/utils/registry/schema"
import { toReadableName } from "@/src/utils/common"
import { ensureAuthForPaidComponents } from "../utils/auth-check"
import { PAID_PLANS, type Plan } from "../utils/registry/schema"
import { getProjectInfo } from "@/src/utils/get-project-info"
import {
  showNotionEnvSetupMessage,
  showNotionTemplateSetupMessage,
  showStylesConfigurationMessage,
  showTableNodeSetupMessage,
} from "@/src/utils/prompt-messages"
import {
  createWelcomeBanner,
  logLines,
  createExplanationSection,
  clearTerminal,
  createSpacedDivider,
} from "@/src/utils/cli-ui"
import {
  autoImportStyles,
  logAutoImportResult,
} from "@/src/utils/auto-import-styles"

export const addOptionsSchema = z.object({
  components: z.array(z.string()).optional(),
  cwd: z.string(),
  path: z.string().optional(),
  silent: z.boolean(),
  overwrite: z.boolean(),
})

type AddOptions = z.infer<typeof addOptionsSchema>

interface CategoryMap {
  templates: RegistryItemIndexSchema
  newComponents: RegistryItemIndexSchema
  ui: RegistryItemIndexSchema
  primitives: RegistryItemIndexSchema
  uiUtils: RegistryItemIndexSchema
  nodes: RegistryItemIndexSchema
}

const PROMPT_PAGE_SIZE = 1_000 // Large size to avoid help tip
const TEMPLATE_DESCRIPTION_MAX_LINE_WIDTH = 92
const TEMPLATE_DESCRIPTION_MIN_LINE_WIDTH = 48
const TEMPLATE_DESCRIPTION_INDENT_WIDTH = 4

const UI = {
  emptyRegistry: colors.red("❌ No components or templates found"),
  operationCancelled: colors.gray("Operation cancelled"),
  missingDirectory: colors.red(
    "❌ Directory not found. Use init to initialize a project first."
  ),
}

const PROMPT_THEME = {
  icon: {
    cursor: colors.cyan("▸"),
    checked: colors.cyan("◉"),
    unchecked: "○",
  },
  style: {
    highlight: (text: string) => colors.cyan(text),
    message: (text: string) => text,
    answer: (text: string) => "", // Hide inline answer display
  },
  prefix: {
    done: colors.cyan("✔"),
    idle: colors.cyan("?"),
  },
}

function normalizeDescription(description?: string): string {
  const normalized = description?.replace(/\s+/g, " ").trim()
  return normalized ?? ""
}

function wrapText(text: string, maxLineLength: number): string[] {
  const words = text.split(" ")
  const lines: string[] = []
  let currentLine = ""

  for (const word of words) {
    const nextLine = currentLine ? `${currentLine} ${word}` : word

    if (nextLine.length > maxLineLength && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = nextLine
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

function getTemplateDescriptionLineWidth(): number {
  const terminalWidth = process.stdout.columns

  if (!terminalWidth) {
    return TEMPLATE_DESCRIPTION_MAX_LINE_WIDTH
  }

  return Math.max(
    TEMPLATE_DESCRIPTION_MIN_LINE_WIDTH,
    Math.min(
      TEMPLATE_DESCRIPTION_MAX_LINE_WIDTH,
      terminalWidth - TEMPLATE_DESCRIPTION_INDENT_WIDTH
    )
  )
}

function formatTemplatePlanLabel(plans?: Plan[]): string {
  const planLabel = getPlanLabel(plans)

  if (plans?.includes("light") || plans?.includes("open-source")) {
    return "Open Source - MIT"
  }

  return planLabel
}

function formatTemplateChoiceName(template: RegistryItemIndexSchema[number]) {
  const planLabel = formatTemplatePlanLabel(template.plans)
  const name = `${colors.bold(toReadableName(template.name))} ${colors.dim(
    `(${planLabel})`
  )}:`
  const description = normalizeDescription(template.description)

  if (!description) {
    return name
  }

  const descriptionLines = wrapText(
    description,
    getTemplateDescriptionLineWidth()
  ).map((line) => `  ${line}`)

  return [name, ...descriptionLines].join("\n")
}

function getPlanLabel(plans?: Plan[]): string {
  if (!plans?.length) {
    return "Free"
  }

  if (plans.includes("light") || plans.includes("open-source")) {
    return "Open source (MIT)"
  }

  const requiredPlan = PAID_PLANS.find((plan) => plans.includes(plan))
  if (!requiredPlan) {
    return "Free"
  }

  const planName = `${requiredPlan[0].toUpperCase()}${requiredPlan.slice(1)}`
  return requiredPlan === "start"
    ? `${planName} plan • Trial`
    : `${planName} plan`
}

/**
 * Creates a themed confirmation prompt with consistent styling
 */
const createThemedConfirm = (message: string, defaultValue: boolean = true) => {
  return confirm({
    message,
    default: defaultValue,
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Filters templates to only include free ones based on registry data
 */
function filterFreeTemplates(
  templates: RegistryItemIndexSchema,
  freeComponents: string[] | null
): RegistryItemIndexSchema {
  if (!freeComponents) {
    return templates
  }

  return templates.filter((template) => freeComponents.includes(template.name))
}

/**
 * Clears specified number of lines from console
 */
function clearPromptLines(lines: number) {
  for (let i = 0; i < lines; i++) {
    process.stdout.write("\x1B[1A") // Move cursor up
    process.stdout.write("\x1B[2K") // Clear line
  }
}

/**
 * Categorizes registry items by type
 */
const categorizeRegistryItems = (
  registryIndex: RegistryItemIndexSchema
): CategoryMap => {
  return {
    templates: registryIndex.filter(
      (entry) => entry.type === "registry:template"
    ),
    newComponents: registryIndex.filter((entry) => entry.isNew === true),
    ui: registryIndex.filter((entry) => entry.type === "registry:ui"),
    primitives: registryIndex.filter(
      (entry) => entry.type === "registry:ui-primitive"
    ),
    uiUtils: registryIndex.filter(
      (entry) => entry.type === "registry:ui-utils"
    ),
    nodes: registryIndex.filter((entry) => entry.type === "registry:node"),
  }
}

/**
 * Prompts user to select between components and templates
 */
async function promptForInitialSelection(
  categories: CategoryMap,
  showDivider = true
): Promise<"components" | "templates" | null> {
  const message = "What would you like to integrate?"

  const choices = []
  const isComponentEmpty =
    categories.ui.length === 0 &&
    categories.nodes.length === 0 &&
    categories.newComponents.length === 0
  const isTemplateEmpty = categories.templates.length === 0

  if (!isTemplateEmpty) {
    choices.push({
      name: "Templates - Full editor applications",
      value: "templates",
    })
  }

  if (!isComponentEmpty) {
    choices.push({
      name: "UI Components - Individual editor building blocks",
      value: "components",
    })
  }

  if (choices.length === 0) {
    logger.break()
    console.log(UI.emptyRegistry)
    return null
  }

  try {
    if (showDivider) {
      console.log(createSpacedDivider())
    }

    const selection = await select({
      message: colors.reset(message),
      pageSize: PROMPT_PAGE_SIZE,
      theme: PROMPT_THEME,
      choices,
    })

    if (showDivider) {
      console.log(createSpacedDivider())
    }

    return selection as "templates" | "components"
  } catch (error) {
    clearPromptLines(4)
    console.log(UI.operationCancelled)
    return null
  }
}

/**
 * Creates choices for component menu with appropriate sections
 */
async function createComponentChoices(
  categories: CategoryMap,
  components: string[] | null
): Promise<Array<{ name: string; value: string } | Separator>> {
  const choices: Array<{ name: string; value: string } | Separator> = []

  if (!components) {
    return choices
  }

  const addCategorySection = (
    items: RegistryItemIndexSchema,
    title: string
  ) => {
    const filteredItems = items.filter((item) => components.includes(item.name))

    if (filteredItems.length > 0) {
      choices.push(new Separator(""))
      choices.push(new Separator(colors.whiteBold(`  ${title}`)))

      filteredItems.forEach((item) => {
        const planLabel = getPlanLabel(item.plans)

        const name = toReadableName(item.name)
        const paddedName = name.padEnd(35)

        choices.push({
          name: `${paddedName} ${colors.dim(planLabel)}`,
          value: item.name,
        })
      })

      choices.push(new Separator(" "))
    }
  }

  const addNewComponentsSection = (
    items: RegistryItemIndexSchema,
    title: string
  ) => {
    const filteredItems = items.filter((item) => components.includes(item.name))

    if (filteredItems.length > 0) {
      choices.push(new Separator(""))
      choices.push(new Separator(colors.whiteBold(`  ${title}`)))

      filteredItems.forEach((item) => {
        const planLabel = getPlanLabel(item.plans)

        const name = toReadableName(item.name)
        const paddedName = name.padEnd(35)
        const description = item.description
          ? `\n   ${colors.dim(item.description)}`
          : ""

        choices.push({
          name: `${paddedName} ${colors.dim(planLabel)}${description}`,
          value: item.name,
        })
      })

      choices.push(new Separator(" "))
    }
  }

  addNewComponentsSection(categories.newComponents, "NEW COMPONENTS")
  addCategorySection(categories.ui, "UI COMPONENTS")
  addCategorySection(categories.nodes, "NODE COMPONENTS")
  addCategorySection(categories.primitives, "PRIMITIVES")

  return choices
}

/**
 * Shows menu for selecting components
 */
async function componentMenu(
  categories: CategoryMap,
  components: string[] | null
): Promise<string[]> {
  const choices = await createComponentChoices(categories, components)

  if (choices.length === 0) {
    return []
  }

  try {
    logger.log("")
    logger.log(
      colors.cyan(
        "  Press Space to select • A to toggle all • Enter to confirm"
      )
    )

    const selectedComponents = await checkbox({
      message: "Choose UI components to install:",
      pageSize: 20,
      choices,
      theme: PROMPT_THEME,
      validate: (items) => {
        if (items.length === 0) {
          return "Select at least one component (use Space to select, then Enter to confirm)"
        }
        return true
      },
    })

    // Display selected components
    if (selectedComponents && selectedComponents.length > 0) {
      logger.log("")
      logger.log(colors.cyan("Selected components:"))
      selectedComponents.forEach((component) => {
        logger.log(`  ${colors.cyan("•")} ${toReadableName(component)}`)
      })
    }

    logger.log("")
    return selectedComponents || []
  } catch (error) {
    clearPromptLines(25)
    console.log(UI.operationCancelled)
    return []
  }
}

/**
 * Shows menu for selecting templates
 */
async function templateMenu(
  templates: RegistryItemIndexSchema
): Promise<string[]> {
  try {
    const choices = templates.map((template) => {
      return {
        name: formatTemplateChoiceName(template),
        value: template.name,
      }
    })

    logger.log("")

    const selectedTemplate = await select({
      message: "Choose an editor template to install:",
      pageSize: 20,
      choices,
      theme: PROMPT_THEME,
    })

    // Display selected template
    if (selectedTemplate) {
      logger.log("")
      logger.log(colors.cyan("Selected template:"))
      logger.log(`  ${colors.cyan("•")} ${toReadableName(selectedTemplate)}`)
    }

    logger.log("")
    return selectedTemplate ? [selectedTemplate] : []
  } catch (error) {
    clearPromptLines(25)
    console.log(UI.operationCancelled)
    return []
  }
}

/**
 * Main function to prompt for registry components
 */
export async function promptForRegistryComponents(
  options: AddOptions,
  showDivider = true
): Promise<string[]> {
  if (options.components?.length) {
    return options.components
  }

  let registryIndex
  try {
    registryIndex = await getRegistryIndex()
  } catch (error) {
    // Check if this is a 401 authentication error
    if (
      error instanceof Error &&
      error.message.includes("You are not authorized")
    ) {
      // Assume the component is paid and trigger authentication flow
      const isAuthenticated = await ensureAuthForPaidComponents(
        [{ name: "unknown", plans: ["start" as Plan] }], // Assume paid component
        options.cwd
      )

      if (!isAuthenticated) {
        logger.error(
          "Authentication failed. Cannot proceed with component selection."
        )
        return []
      }

      // Retry registry fetch after authentication
      try {
        registryIndex = await getRegistryIndex()
      } catch (retryError) {
        logger.break()
        handleError(
          new Error(
            "[prompts] - Failed to fetch registry index after authentication."
          )
        )
        return []
      }
    } else {
      logger.break()
      handleError(error)
      return []
    }
  }

  if (!registryIndex) {
    logger.break()
    handleError(new Error("[prompts] - Failed to fetch registry index."))
    return []
  }

  const categories = categorizeRegistryItems(registryIndex)
  const selection = await promptForInitialSelection(categories, showDivider)

  if (!selection) {
    return []
  }

  const allComponents = registryIndex
    .filter((item) => {
      const hide = item.hide ?? false
      return !hide
    })
    .map((item) => item.name)

  switch (selection) {
    case "components":
      return (await componentMenu(categories, allComponents)) || []
    case "templates": {
      const filteredTemplates = filterFreeTemplates(
        categories.templates,
        allComponents
      )
      const templateResults = await templateMenu(filteredTemplates)
      return templateResults || []
    }
    default:
      return []
  }
}

export const add = new Command()
  .name("add")
  .description(
    "add Tiptap UI components or templates as source code to your project"
  )
  .argument("[components...]", "the components to add")
  .option("-o, --overwrite", "overwrite existing files.", false)
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd()
  )
  .option("-p, --path <path>", "the path to add the component to.")
  .option("-s, --silent", "mute output.", false)
  .action(async (components, opts) => {
    try {
      // Clear terminal for a fresh start
      clearTerminal()

      // Show welcome banner
      const welcomeBanner = createWelcomeBanner(
        "Tiptap UI Components",
        "Install UI components or templates as editable source code in your project"
      )
      logLines(welcomeBanner)

      // Explain what Tiptap CLI does
      const explanation = createExplanationSection(
        "What are Tiptap UI Components?",
        [
          "React UI components that help you develop an editor",
          "They install as source code (not npm packages) in your src/components/",
          "UI components are a foundation for customization rather than a fixed library",
        ]
      )
      logLines(explanation)

      // Wait for user to press Enter
      await input({
        message: colors.reset(
          `Press ${colors.cyan("Enter")} to add components or templates...`
        ),
        theme: {
          prefix: {
            done: "",
            idle: "",
          },
        },
      })

      // Add visual separation
      logger.log("")
      logger.log("")
      logger.log("")

      const options = addOptionsSchema.parse({
        components,
        cwd: path.resolve(opts.cwd),
        ...opts,
      })

      if (!options.components?.length) {
        options.components = await promptForRegistryComponents(options)
      }

      // No components selected
      if (!options.components?.length) {
        return
      }

      const registryIndex = await getRegistryIndex()
      if (!registryIndex) {
        throw new Error("Failed to fetch registry index.")
      }

      // Get details of selected components
      const selectedComponentDetails = options.components.map(
        (componentName) => {
          const componentInfo = registryIndex.find(
            (item) => item.name === componentName
          )
          return {
            name: componentName,
            plans: componentInfo?.plans || [],
          }
        }
      )

      const isAuthenticated = await ensureAuthForPaidComponents(
        selectedComponentDetails,
        options.cwd
      )
      if (!isAuthenticated) {
        logger.error(
          "Authentication failed. Cannot proceed with paid component download."
        )
        logger.log(
          "You can try again with only free components, or authenticate first."
        )
        process.exit(1)
      }

      const { errors, config } = await preFlightAdd(options)

      if (errors[ERRORS.MISSING_DIR_OR_EMPTY_PROJECT]) {
        logger.warn(`\n${UI.missingDirectory}`)
        process.exit(0)
      }

      if (!config) {
        throw new Error(`Failed to read config at ${colors.cyan(options.cwd)}.`)
      }

      const created = await addComponents(options.components, config, options)

      if (!created) {
        throw new Error("Component addition failed.")
      }

      // Check if any files were processed (created, updated, or skipped)
      const totalFilesProcessed =
        (created.filesCreated?.length || 0) +
        (created.filesUpdated?.length || 0) +
        (created.filesSkipped?.length || 0)

      // Show success message if components were processed
      if (created && totalFilesProcessed > 0) {
        // Check all file arrays for template detection
        const allFiles = [
          ...(created.filesCreated || []),
          ...(created.filesUpdated || []),
          ...(created.filesSkipped || []),
        ]

        const containsNotion = allFiles.some((path) => path.includes("notion"))
        const containsSimpleEditor = allFiles.some(
          (path) => path.includes("simple") && path.includes("editor")
        )

        if (containsNotion) {
          // Show single-line success message
          logger.log("")
          if (created.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Notion template installed - ${created.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else {
            logger.log(
              `${colors.green("✔")} Notion template already installed - ${created.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Auto-import styles and show env setup for Notion template
          const projectInfo = await getProjectInfo(options.cwd)
          if (projectInfo) {
            const autoImportResult = await autoImportStyles(
              created,
              config,
              projectInfo.framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showNotionTemplateSetupMessage(projectInfo.framework, config)
            } else {
              // Show only env setup since styles were auto-imported
              showNotionEnvSetupMessage(projectInfo.framework)
            }
          }
        } else if (containsSimpleEditor) {
          // Show single-line success message
          logger.log("")
          if (created.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Simple editor template installed - ${created.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else {
            logger.log(
              `${colors.green("✔")} Simple editor template already installed - ${created.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Auto-import styles
          const projectInfo = await getProjectInfo(options.cwd)
          if (projectInfo) {
            const autoImportResult = await autoImportStyles(
              created,
              config,
              projectInfo.framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showStylesConfigurationMessage(projectInfo.framework, config)
            }
          }
        } else {
          // Show single-line success message
          logger.log("")
          if (created.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Components installed - ${created.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else if (created.filesSkipped.length > 0) {
            logger.log(
              `${colors.green("✔")} Components already installed - ${created.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Auto-import styles if components were added
          const projectInfo = await getProjectInfo(options.cwd)
          if (projectInfo) {
            const autoImportResult = await autoImportStyles(
              created,
              config,
              projectInfo.framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showStylesConfigurationMessage(projectInfo.framework, config)
            }
          }

          // Show Table Node integration guide if table-node was installed
          const containsTableNode = options.components.includes("table-node")
          if (containsTableNode) {
            showTableNodeSetupMessage()
          }
        }
      }
    } catch (error) {
      logger.break()
      handleError(error)
    }
  })
